import { useState, useEffect } from 'react';
import api from '../utils/api';

/**
 * Returns issue tracker configuration for a project.
 *
 * When type === 'none' or enabled === false:
 *   - enabled: false  → all tracker UI (JIRA columns, Link/Sync buttons) hidden
 *
 * When type === 'jira' and enabled:
 *   - labels use JIRA terminology, full JIRA UI shown
 *
 * When type === 'other' and enabled:
 *   - labels use the project's custom tracker names (e.g. "GitHub Issue")
 *   - JIRA-specific UI (Search JIRA, Create JIRA issue) is hidden
 *   - Generic fields shown: External ID, External URL, External Status
 */
export function useIssueTracker(projectId) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/projects/${projectId}/issue-tracker`)
      .then(res => { if (!cancelled) setConfig(res.data.data); })
      .catch(() => { if (!cancelled) setConfig({ type: 'none', enabled: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Derived labels and flags
  const isJira = config?.type === 'jira' && config?.enabled;
  const isOther = config?.type === 'other' && config?.enabled;
  const isEnabled = isJira || isOther;

  const labels = {
    trackerName:  isJira ? 'JIRA'          : (config?.trackerName  || 'External Tracker'),
    idLabel:      isJira ? 'JIRA Issue Key' : (config?.idLabel      || 'External ID'),
    urlLabel:     isJira ? 'JIRA URL'       : (config?.urlLabel     || 'External URL'),
    statusLabel:  isJira ? 'JIRA Status'    : (config?.statusLabel  || 'External Status'),
    linkAction:   isJira ? 'Link JIRA Issue': `Link ${config?.trackerName || 'External'} Issue`,
    createAction: isJira ? 'Create JIRA Issue': `Log ${config?.trackerName || 'External'} Issue`,
    syncAction:   isJira ? 'Sync from JIRA' : `Sync from ${config?.trackerName || 'External Tracker'}`,
    columnHeader: isJira ? 'JIRA'           : (isOther ? (config?.trackerName || 'Tracker') : null),
  };

  return { config, loading, isEnabled, isJira, isOther, labels };
}
